import React, { useEffect, useState } from 'react';
import { Box, FormControl } from '@mui/material';
import { useAuth } from '../../security/AuthProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {AuthErrorCode, getErrorMessage} from "../../api/types";
import { resendAccountSetupRequest } from '../../api/AuthApi';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import { AuthFormContainer } from '../../components/AuthFormContainer';
import { ErrorAlert } from '../../components/ErrorAlert';
import Spinner from '../../ui/Spinner';

interface AccountSetupProps {}

const SetupAccount: React.FC<AccountSetupProps> = () => {

    const { validateAccountSetupToken, completeAccountSetup } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState<string>();
    const [password, setPassword] = useState<string>('');
    const [confirmedPassword, setConfirmedPassword] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
    const [username, setUsername] = useState<string>();
    const [tokenValid, setTokenValid] = useState(false);

    useEffect(() => {
        const tokenFromUrl = searchParams.get('token');
        if (tokenFromUrl) {
            setToken(tokenFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        if (token) {
            validateToken(token);
        }
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token || !password || !confirmedPassword) return;

        setLoading(true);
        setError(null);
        setErrorCode(null);

        try {
            const result = await completeAccountSetup(password, confirmedPassword, token);

            if (result.success) {
                navigate('/login', {
                    replace: true,
                    state: { message: 'Konto erfolgreich eingerichtet. Bitte melden Sie sich an.' }
                });
            } else {
                const code = result.errorCode || AuthErrorCode.UNEXPECTED_ERROR;
                setErrorCode(code);
                setError(getErrorMessage(code));
            }
        } catch (err) {
            console.error('Account setup error:', err);
            setErrorCode(AuthErrorCode.UNEXPECTED_ERROR);
            setError(getErrorMessage(AuthErrorCode.UNEXPECTED_ERROR));
        } finally {
            setLoading(false);
        }
    };

    const validateToken = async (token: string) => {
        if (!token) return;

        setValidatingToken(true);
        setError(null);
        setErrorCode(null);

        try {
            const result = await validateAccountSetupToken(token);

            if (result.success) {
                setTokenValid(true);
                setError(null);
                setErrorCode(null);

            } else {
                setTokenValid(false);
                const code = result.errorCode || AuthErrorCode.UNEXPECTED_ERROR;
                setErrorCode(code);
                setError(getErrorMessage(code));
            }
        } catch (err) {
            console.error('Token validation error:', err);
            setTokenValid(false);
            setErrorCode(AuthErrorCode.UNEXPECTED_ERROR);
            setError(getErrorMessage(AuthErrorCode.UNEXPECTED_ERROR));
        } finally {
            setValidatingToken(false);
        }
    };

    const handleResendAccountSetup = async () => {
        if (!username) return;
        setLoading(true);

        try {
            await resendAccountSetupRequest({ username });
        } catch (err) {
            console.error('Failed to resend account setup:', err);
        } finally {
            setLoading(false);
        }
    };

    const showResendLink = !!error && !!username && (
        errorCode === AuthErrorCode.EXPIRED_TOKEN ||
        errorCode === AuthErrorCode.TOKEN_ALREADY_USED ||
        errorCode === AuthErrorCode.TOKEN_INVALIDATED
    );

    const formDisabled = !tokenValid;

    if (validatingToken) {
        return <Spinner />;
    }

    return (
        <AuthFormContainer title="Nur noch ein letzter Schritt">
            {error && (
                <ErrorAlert
                    message={error}
                    action={
                        showResendLink
                            ? {
                                label: 'Klicken Sie hier, um den Link erneut zu versenden',
                                onClick: handleResendAccountSetup
                            }
                            : undefined
                    }
                />
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <FormControl fullWidth required>
                    <TextField
                        isDisabled={formDisabled}
                        autoFocus={true}
                        id="password"
                        name="password"
                        placeholder="Passwort"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e)}
                    />
                </FormControl>

                <FormControl fullWidth required sx={{ mt: 2 }}>
                    <TextField
                        isDisabled={formDisabled}
                        id="confirm-password"
                        name="confirm-password"
                        placeholder="Passwort bestätigen"
                        type="password"
                        value={confirmedPassword}
                        onChange={(e) => setConfirmedPassword(e)}
                    />
                </FormControl>

                <Button
                    isDisabled={formDisabled}
                    fullWidth={true}
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="mt-4"
                    loading={loading}
                >
                    Einrichtung abschließen
                </Button>
            </Box>
        </AuthFormContainer>
    );
};

export default SetupAccount;